/**
 * CR-8720: the bounded inputs on the NCR Credit and Replacement tabs.
 *
 * `MoneyInput` shows the currency, allows 0.00 or more with two decimals and
 * names the order value it can't exceed. `QuantityInput` allows whole numbers
 * from 0 up to the ordered quantity. Both validate as the user types and on
 * blur, and tell the parent whether the value is acceptable, so the submit
 * button can hold until it is. The server applies the same rules.
 */

import { useState } from 'react';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  moneyError, moneyHint, normaliseMoney, quantityError, quantityHint, type MoneyRule,
} from './claimLimits';

/** 'Credit Amount' -> 'Credit amount': messages use the server's sentence case. */
function sentenceCase(label: string): string {
  return label.charAt(0) + label.slice(1).toLowerCase();
}

interface MoneyInputProps {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  rule: MoneyRule;
  required?: boolean;
  placeholder?: string;
  /** Called with the current message (null = acceptable) whenever it changes. */
  onValidity?: (error: string | null) => void;
}

/**
 * A currency amount: `{currency} [ 0.00 ]`, min 0, step 0.01, capped at the order value.
 *
 * @param rule - the currency to show and the cap to enforce
 */
export function MoneyInput({ id, label, value, onChange, rule, required, placeholder = '0.00', onValidity }: MoneyInputProps) {
  const [error, setError] = useState<string | null>(null);
  const check = (next: string) => {
    const message = moneyError(next, rule, sentenceCase(label));
    setError(message);
    onValidity?.(message);
  };
  const hint = moneyHint(rule);
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}{required && <span className="text-red-500"> *</span>}</Label>
      <div className="flex items-stretch">
        <span className="inline-flex items-center rounded-l-md border border-r-0 border-input bg-muted px-3 text-sm text-muted-foreground"
          data-testid={`${id}-currency`}>
          {rule.currency}
        </span>
        <Input
          id={id}
          type="number"
          inputMode="decimal"
          min="0"
          step="0.01"
          max={rule.limit ?? undefined}
          className="rounded-l-none"
          value={value}
          placeholder={placeholder}
          aria-invalid={!!error}
          aria-describedby={`${id}-hint`}
          onChange={e => { onChange(e.target.value); check(e.target.value); }}
          onBlur={e => {
            const normalised = normaliseMoney(e.target.value);
            if (normalised !== e.target.value) onChange(normalised);
            check(normalised);
          }}
        />
      </div>
      {error ? (
        <p id={`${id}-hint`} role="alert" className="text-sm text-red-600">{error}</p>
      ) : hint ? (
        <p id={`${id}-hint`} className="text-xs text-muted-foreground">{hint}</p>
      ) : null}
    </div>
  );
}

interface QuantityInputProps {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  /** The ordered quantity; null = no cap known. */
  limit: string | null | undefined;
  placeholder?: string;
  /** Called with the current message (null = acceptable) whenever it changes. */
  onValidity?: (error: string | null) => void;
}

/**
 * A whole-number quantity, min 0, step 1, capped at the ordered quantity.
 *
 * @param limit - the ordered quantity from the NCR detail
 */
export function QuantityInput({ id, label, value, onChange, limit, placeholder = '0', onValidity }: QuantityInputProps) {
  const [error, setError] = useState<string | null>(null);
  const check = (next: string) => {
    const message = quantityError(next, limit, sentenceCase(label));
    setError(message);
    onValidity?.(message);
  };
  const hint = quantityHint(limit);
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        type="number"
        inputMode="numeric"
        min="0"
        step="1"
        max={limit ?? undefined}
        value={value}
        placeholder={placeholder}
        aria-invalid={!!error}
        aria-describedby={`${id}-hint`}
        onChange={e => { onChange(e.target.value); check(e.target.value); }}
        onBlur={e => check(e.target.value)}
      />
      {error ? (
        <p id={`${id}-hint`} role="alert" className="text-sm text-red-600">{error}</p>
      ) : hint ? (
        <p id={`${id}-hint`} className="text-xs text-muted-foreground">{hint}</p>
      ) : null}
    </div>
  );
}
